import React, { createContext, useContext, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { paragon } from '@useparagon/connect';

import { useAuth } from '../../authentication';
import { useOrganizations } from './OrganizationsProvider';
import { useCreateLinkTokenMutation, useCreateParagonTokenMutation } from '../hooks';

paragon.configureGlobal({
  host: 'paragon.supper.co'
});

const LinkTokenContext = createContext();

const LinkTokenProvider = ({ children = null }) => {
  const { accessToken } = useAuth();
  const { currentOrganization } = useOrganizations();

  const createLinkTokenMutation = useCreateLinkTokenMutation(accessToken);
  const createParagonTokenMutation = useCreateParagonTokenMutation(accessToken);

  const createLinkToken = useCallback(
    async (integrationName, schema) => {
      const data = await createLinkTokenMutation.mutateAsync({
        organizationId: currentOrganization?.id,
        integrationName,
        schema
      });
      return data.linkToken;
    },
    [createLinkTokenMutation, accessToken, currentOrganization?.id]
  );

  // If the organization_id is in a specific list, use the non-prod project id
  // these match the organization ids in the paragon_keys table
  const devParagonProductionOrganizationIds = [];

  let paragonProjectId = process.env.REACT_APP_PARAGON_PROJECT_ID;
  if (!devParagonProductionOrganizationIds.includes(currentOrganization?.id)) {
    paragonProjectId = process.env.REACT_APP_PARAGON_PROD_PROJECT_ID;
  }

  const createParagonToken = useCallback(async () => {
    const data = await createParagonTokenMutation.mutateAsync(currentOrganization?.id);

    paragon.authenticate(paragonProjectId, data.token);
  }, [createParagonTokenMutation, accessToken, currentOrganization?.id]);

  const value = useMemo(
    () => ({
      createLinkToken,
      createParagonToken
    }),
    []
  );

  return <LinkTokenContext.Provider value={value}>{children}</LinkTokenContext.Provider>;
};

LinkTokenProvider.propTypes = {
  children: PropTypes.node
};

const useLinkToken = () => useContext(LinkTokenContext);

export { LinkTokenProvider, LinkTokenContext, useLinkToken };
